import { ref, readonly } from 'vue'
import { Zenmanage, ConfigBuilder, Context, Attribute } from '@zenmanage/sdk'
import { useSessionStore } from '@/stores/session'

/**
 * Feature flag utility for the Zenmanage web app.
 *
 * Uses the @zenmanage/sdk (v2) to evaluate flags. The SDK fetches rules from the
 * Zenmanage CDN, caches them in memory, and evaluates locally – no per-flag
 * network requests after the initial load.
 *
 * User context is automatically attached from the session store so that
 * percentage rollouts and user-targeting rules work correctly.
 *
 * Override mechanism: set `globalThis.window.__ZENMANAGE_FLAGS__` to an object of key→value
 * pairs for E2E or dev testing without a live API.
 */

/** @type {import('@zenmanage/sdk').FlagManager | null} */
let _flagManager = null

/** Local in-memory cache of already-resolved flag values. */
const _cache = new Map()

/**
 * Lazily create and return the shared FlagManager instance.
 *
 * @returns {import('@zenmanage/sdk').FlagManager | null}
 */
function getFlagManager() {
    if (_flagManager) return _flagManager

    const token = import.meta.env.VITE_ZENMANAGE_TOKEN
    if (!token) return null

    const config = ConfigBuilder.create()
        .withEnvironmentToken(token)
        .withCacheBackend('memory')
        .withUsageReporting(true)
        .build()

    const zenmanage = new Zenmanage(config)
    _flagManager = zenmanage.flags()
    return _flagManager
}

/**
 * Build a user Context from the current session data.
 *
 * Returns null if no user is logged in. The context uses the user's ULID as
 * the identifier (used for percentage-rollout bucketing) and attaches useful
 * attributes (email, role, name, account info) for rule targeting.
 *
 * @returns {import('@zenmanage/sdk').Context | null}
 */
function getUserContext() {
    let sessionStore
    try {
        sessionStore = useSessionStore()
    } catch {
        // Pinia not active yet (e.g. during SSR or early bootstrap)
        return null
    }

    const user = sessionStore.data?.user?.data
    if (!user) return null

    // Use ULID as the stable identifier; fall back to email
    const identifier = user.ulid || user.email
    if (!identifier) return null

    const name =
        [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined

    const context = Context.single('user', identifier, name)

    // Attach attributes for rule targeting
    if (user.email) {
        context.addAttribute(new Attribute('email', [user.email]))
    }
    if (user.effective_role) {
        context.addAttribute(new Attribute('role', [user.effective_role]))
    }
    if (user.first_name) {
        context.addAttribute(new Attribute('first_name', [user.first_name]))
    }
    if (user.last_name) {
        context.addAttribute(new Attribute('last_name', [user.last_name]))
    }

    // Account-level attributes. `status` is the API's current status for the
    // account, which on the new billing system it takes from the subscription
    // (trial, active, active_past, suspended, cancelled), not the column the
    // account was created with. It is read when the context is built, so it
    // is as fresh as the session.
    const account = sessionStore.data?.account?.data
    if (account) {
        if (account.name) {
            context.addAttribute(new Attribute('account_name', [account.name]))
        }
        if (account.status) {
            context.addAttribute(
                new Attribute('account_status', [account.status])
            )
        }
        // Trial accounts share the Team plan row, so report a distinct
        // "Trial" value here regardless of the underlying plan data —
        // otherwise rules targeting account_plan == "Team" would silently
        // include every trial account too.
        if (account.status === 'trial') {
            context.addAttribute(new Attribute('account_plan', ['Trial']))
        } else if (account.plan?.data?.name) {
            context.addAttribute(
                new Attribute('account_plan', [account.plan.data.name])
            )
        }
        if (account.plan?.data?.type) {
            context.addAttribute(
                new Attribute('account_plan_type', [account.plan.data.type])
            )
        }
    }

    return context
}

/**
 * Build a cache key that includes the user context identifier so that
 * flag evaluations are not shared across different users / sessions.
 *
 * @param {string} key - Flag key
 * @param {import('@zenmanage/sdk').Context | null} context
 * @returns {string}
 */
function buildCacheKey(key, context) {
    const id = context?.getIdentifier?.() ?? 'anon'
    return `${key}::${id}`
}

/**
 * Get a raw flag value.
 *
 * @param {string} key   - Flag key
 * @param {*} defaultValue - Fallback value
 * @returns {Promise<*>} Resolved flag value
 */
export async function getFlag(key, defaultValue = false) {
    // Test / dev override
    if (
        globalThis.window !== undefined &&
        globalThis.window.__ZENMANAGE_FLAGS__
    ) {
        const override = globalThis.window.__ZENMANAGE_FLAGS__[key]
        if (override !== undefined) return override
    }

    // Attach user context for percentage-rollout bucketing & targeting
    const context = getUserContext()
    const cacheKey = buildCacheKey(key, context)

    // Local cache hit (keyed by flag + user so context changes are respected)
    if (_cache.has(cacheKey)) return _cache.get(cacheKey)

    try {
        const manager = getFlagManager()
        if (!manager) return defaultValue

        const scoped = context ? manager.withContext(context) : manager

        const flag = await scoped.single(key, defaultValue)
        const value = flag.getValue()
        _cache.set(cacheKey, value)
        return value
    } catch {
        return defaultValue
    }
}

/**
 * Evaluate a boolean feature flag.
 *
 * @param {string} key - Flag key
 * @param {boolean} defaultValue - Fallback (default: false)
 * @returns {Promise<boolean>}
 */
export async function isFeatureEnabled(key, defaultValue = false) {
    const value = await getFlag(key, defaultValue)
    return value === true
}

/**
 * Force-refresh the SDK rules and clear the local cache.
 *
 * @returns {Promise<void>}
 */
export async function refreshFlags() {
    _cache.clear()
    try {
        const manager = getFlagManager()
        if (manager) await manager.refreshRules()
    } catch {
        // Silently ignore refresh failures.
    }
}

/**
 * Clear the local flag value cache so the next `getFlag` call re-evaluates
 * via the SDK.
 */
export function clearFlagCache() {
    _cache.clear()
}

/**
 * Vue composable for feature flags.
 *
 * Returns a reactive `enabled` ref that resolves asynchronously via the SDK.
 *
 * Usage:
 *   const { enabled, loading } = useFeatureFlag('my-flag', false)
 *
 * @param {string} key - Flag key
 * @param {*} defaultValue - Fallback value
 * @returns {{ enabled: import('vue').Ref<boolean>, loading: import('vue').Ref<boolean> }}
 */
export function useFeatureFlag(key, defaultValue = false) {
    const enabled = ref(defaultValue === true)
    const loading = ref(true)

    // Test / dev override — resolve immediately
    if (
        globalThis.window !== undefined &&
        globalThis.window.__ZENMANAGE_FLAGS__
    ) {
        const override = globalThis.window.__ZENMANAGE_FLAGS__[key]
        if (override !== undefined) {
            enabled.value = override === true
            loading.value = false
            return { enabled: readonly(enabled), loading: readonly(loading) }
        }
    }

    getFlag(key, defaultValue)
        .then((value) => {
            enabled.value = value === true
        })
        .catch(() => {
            enabled.value = defaultValue === true
        })
        .finally(() => {
            loading.value = false
        })

    return { enabled: readonly(enabled), loading: readonly(loading) }
}

/**
 * Expose internals for testing only.
 */
export const _test = {
    get flagCache() {
        return _cache
    },
    getUserContext,
    resetInstance() {
        _flagManager = null
        _cache.clear()
    },
}
